import React, { useEffect, useState } from "react";
import moment from "moment";
import Wrapper from "../components/Layout/Wrapper";
import styled from "styled-components";
import "react-markdown-editor-lite/lib/index.css";
import StarCounter from "../components/StarCounter";
import LevelBadge from "../components/LevelBadge";
import firebase from "firebase";
import { Award, Student } from "../types";
import useClasses from "../hooks/useClasses";
import useAuth from "../hooks/useAuth";

const ContentPageWrapper = styled.div`
  background-image: linear-gradient(180deg, #235390 0%, #3476cb 100%);
  min-height: 100vh;
  padding-bottom: 32px;
  .sub-header {
    margin-bottom: 16px;
  }
  main {
    width: 100%;
    margin-right: 5%;
  }

  h1 {
    color: white;
  }

  .leaderboard {
    padding-bottom: 64px;
  }

  .leaderboard--options {
    display: flex;
    justify-content: space-between;
    div {
      display: flex;
    }
    margin-bottom: 16px;
    h3 {
      color: white;
      text-transform: uppercase;
      opacity: 0.6;
      font-weight: 700;
      cursor: pointer;

      &.active {
        opacity: 1;
      }
    }
  }

  .leaderboard--row {
    width: 100%;
    background: white;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    margin-bottom: 32px;

    .leaderboard--left,
    .leaderboard--right {
      display: flex;
      align-items: center;
      margin-left: 16px;
    }

    .leaderboard--right {
      margin-right: 16px;
    }
  }

  .leaderboard--number {
    background: #d86403;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-size: 18px;
    font-weight: bold;
    margin-right: 48px;
    margin-left: 24px;
  }

  .leaderboard--name {
    font-size: 18px;
  }

  .leaderboard--row.place-1 {
    .leaderboard--number {
      background: #ffc700;
    }
  }
  .leaderboard--row.place-2 {
    .leaderboard--number {
      background: #d6d6d6;
    }
  }
  .leaderboard--row.place-3 {
    .leaderboard--number {
      background: #d86403;
    }
  }

  .level-badge-icon {
    width: 100px;
    margin-top: -36px;
    margin-bottom: -32px;
  }
  .leaderboard--stars {
    margin: 4px 0;
    margin-right: 16px;
  }
`;

type LeaderboardInfo = {
  id: string;
  name: string;
  klass?: string[];
  awards?: Award[];
};

type LeaderboardData = {
  id: string;
  name: string;
  stars: number;
  placement?: number;
  klass?: string[];
  totalStars: number;
};

const Leaderboard = () => {
  const [status, setStatus] = useState<"idle" | "loading" | "loaded">("idle");
  const [leaderboardInfo, setLeaderboardInfo] = useState<LeaderboardInfo[]>([]);
  const [quantity, setQuantity] = useState<"all" | "class">("class");
  const [time, setTime] = useState<"all" | "week">("week");
  const { data: classData } = useClasses();
  const { accountDetails } = useAuth();

  const userClasses =
    accountDetails?.isSuperuser || accountDetails?.isTeacher
      ? classData?.filter((x) =>
          x.teachers?.some((y) => y.teacherId === accountDetails?.uid)
        )
      : classData?.filter((x) =>
          x.students?.some((y) => y.id === accountDetails?.id)
        );

  useEffect(() => {
    if (status === "loaded" || status === "loading") {
      return;
    }
    setStatus("loading");
    firebase
      .firestore()
      .collection("students")
      .get()
      .then((querySnapshot) => {
        const nextLeaderboardInfo: LeaderboardInfo[] = [];
        querySnapshot.forEach((doc) => {
          const student = doc.data() as Student;
          nextLeaderboardInfo.push({
            id: student.id,
            awards: student.awards,
            name: student.name,
            klass: classData
              ?.filter((x) => x.students?.some((y) => y.id === student.id))
              .map((x) => x.id),
          });
        });
        setStatus("loaded");
        setLeaderboardInfo(nextLeaderboardInfo || []);
      });
  }, [classData, status]);

  const leaderboardData: LeaderboardData[] = leaderboardInfo.reduce(
    (final, info) => {
      const base: LeaderboardData = {
        id: info.id,
        name: info.name,
        totalStars: (info.awards || []).reduce(
          (total, aw) => total + aw.stars,
          0
        ),
        klass: info.klass,
        stars: (info.awards || [])
          .filter((x) =>
            time === "week"
              ? moment(x.date).isBetween(
                  moment().startOf("w"),
                  moment().endOf("w")
                )
              : true
          )
          .reduce((total, aw) => total + aw.stars, 0),
      };

      return [...final, base];
    },
    [] as LeaderboardData[]
  );

  const leaderboardWithPlacements = leaderboardData
    .sort((a, b) => b.stars - a.stars)
    .filter((x) =>
      quantity === "class"
        ? (x.klass || []).some((k) => userClasses?.some((c) => c.id === k))
        : true
    );

  return (
    <ContentPageWrapper>
      <Wrapper style={{ display: "block", margin: "0 auto", paddingTop: 64 }}>
        <h1>Leaderboard</h1>
        <div className="leaderboard--options">
          <div>
            <h3
              className={quantity === "class" ? "active" : ""}
              style={{ marginRight: 32 }}
              onClick={() => setQuantity("class")}
            >
              Your class
            </h3>
            <h3
              className={quantity === "all" ? "active" : ""}
              onClick={() => setQuantity("all")}
            >
              Everyone
            </h3>
          </div>
          <div>
            <h3
              className={time === "week" ? "active" : ""}
              style={{ marginRight: 32 }}
              onClick={() => setTime("week")}
            >
              This week
            </h3>
            <h3
              className={time === "all" ? "active" : ""}
              onClick={() => setTime("all")}
            >
              All Time
            </h3>
          </div>
        </div>
        <div className="leaderboard">
          {leaderboardWithPlacements.map((x) => (
            <div className={`leaderboard--row place-${x.placement}`}>
              <div className="leaderboard--left">
                <div className="leaderboard--number">
                  <LevelBadge stars={x.totalStars} />
                </div>
                <div className="leaderboard--name">{x.name}</div>
              </div>
              <div className="leaderboard--right">
                <div className="leaderboard--stars">
                  <StarCounter stars={x.stars} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Wrapper>
    </ContentPageWrapper>
  );
};

export default Leaderboard;
